import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { contactNumberError, maxCharLimitExceededError } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { agEnterPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { MAX_NUMBER_OF_RESPONDENTS_DIGITS, numberOfRespondentsError } from './attorneyGeneralHelpers';

const MAX_NAME = 2000;
const MAX_SHORT = 255;
const MAX_CONTACT = 10;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function AttorneyGeneralStep8PetitionerDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { respondentTypes, loading: respondentTypesLoading } = useAttorneyGeneralLookups('step8');

  const respondentTypeOptions = useMemo(
    () => toOpts(mergeLookupItems(respondentTypes, draft.respondentType, draft.respondentTypeName)),
    [draft.respondentType, draft.respondentTypeName, respondentTypes],
  );

  useEffect(() => {
    if (draft.representedByAttorney === 'yes' || draft.representedByAttorney === 'no') return;
    setDraft((d) => ({ ...d, representedByAttorney: 'no' }));
  }, [draft.representedByAttorney, setDraft]);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(8, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={8} draft={draft}>
      <SectionCard title={f.sectionPetitionerDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.petitionerName}</label>
            <input
              id="ag-petitionerName"
              className={inpClass(Boolean(errors.petitionerName))}
              type="text"
              value={draft.petitionerName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, petitionerName: maxLen('petitionerName', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('Petitioner Name')}
            />
            <FieldError message={errors.petitionerName} />
          </div>
          <div className="ccr-fg">
            <label>{f.nic}</label>
            <input
              id="ag-petitionerNic"
              className={inpClass(Boolean(errors.petitionerNic))}
              type="text"
              value={draft.petitionerNic}
              onChange={(e) => {
                setDraft((d) => ({ ...d, petitionerNic: maxLen('petitionerNic', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('NIC')}
            />
            <FieldError message={errors.petitionerNic} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.address}</label>
          <textarea
            id="ag-petitionerAddress"
            className={inpClass(Boolean(errors.petitionerAddress))}
            rows={3}
            value={draft.petitionerAddress}
            onChange={(e) => {
              setDraft((d) => ({ ...d, petitionerAddress: maxLen('petitionerAddress', e.target.value, MAX_NAME) }));
            }}
            placeholder={agEnterPlaceholder('Address')}
          />
          <FieldError message={errors.petitionerAddress} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.contactNumber}</label>
            <input
              id="ag-petitionerContact"
              className={inpClass(Boolean(errors.petitionerContact))}
              type="text"
              inputMode="numeric"
              value={draft.petitionerContact}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                setDraft((d) => ({ ...d, petitionerContact: digits }));
                const err = contactNumberError(digits);
                setErrors((prev) => ({ ...prev, petitionerContact: err ?? '' }));
              }}
              placeholder={agEnterPlaceholder('Contact Number')}
            />
            <FieldError message={errors.petitionerContact} />
          </div>
          <div className="ccr-fg">
            <label>{f.emailAddress}</label>
            <input
              id="ag-petitionerEmail"
              className={inpClass(Boolean(errors.petitionerEmail))}
              type="email"
              value={draft.petitionerEmail}
              onChange={(e) => {
                setDraft((d) => ({ ...d, petitionerEmail: maxLen('petitionerEmail', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('Email Address')}
            />
            <FieldError message={errors.petitionerEmail} />
          </div>
        </div>

        <div className="ccr-fg" id="ag-representedByAttorney" style={{ marginBottom: 14 }}>
          <label>{f.representedByAttorney}</label>
          <div className="ccr-radio-row">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="ag-representedByAttorney"
                  checked={draft.representedByAttorney === v}
                  onChange={() => {
                    setDraft((d) => ({
                      ...d,
                      representedByAttorney: v,
                      ...(v === 'no' ? { attorneyName: '', attorneyContact: '' } : {}) }));
                    clearErr('attorneyName');
                    clearErr('attorneyContact');
                  }}
                />
                {v === 'yes' ? 'Yes' : 'No'}
              </label>
            ))}
          </div>
        </div>

        {draft.representedByAttorney === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>{f.attorneyName}</label>
              <input
                id="ag-attorneyName"
                className={inpClass(Boolean(errors.attorneyName))}
                type="text"
                value={draft.attorneyName}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, attorneyName: maxLen('attorneyName', e.target.value, MAX_SHORT) }));
                }}
                placeholder={agEnterPlaceholder('Attorney Name')}
              />
              <FieldError message={errors.attorneyName} />
            </div>
            <div className="ccr-fg">
              <label>{f.attorneyContact}</label>
              <input
                id="ag-attorneyContact"
                className={inpClass(Boolean(errors.attorneyContact))}
                type="text"
                inputMode="numeric"
                value={draft.attorneyContact}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                  setDraft((d) => ({ ...d, attorneyContact: digits }));
                  const err = contactNumberError(digits);
                  setErrors((prev) => ({ ...prev, attorneyContact: err ?? '' }));
                }}
                placeholder={agEnterPlaceholder('Attorney Contact')}
              />
              <FieldError message={errors.attorneyContact} />
            </div>
          </div>
        ) : null}

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.respondentNames}</label>
            <input
              id="ag-respondentNames"
              className={inpClass(Boolean(errors.respondentNames))}
              type="text"
              value={draft.respondentNames}
              onChange={(e) => {
                setDraft((d) => ({ ...d, respondentNames: maxLen('respondentNames', e.target.value, MAX_SHORT) }));
              }}
              placeholder={agEnterPlaceholder('Respondent Name(s)')}
            />
            <FieldError message={errors.respondentNames} />
          </div>
          <div className="ccr-fg">
            <label>{f.respondentType}</label>
            <WizardSelect
              id="ag-respondentType"
              hasError={Boolean(errors.respondentType)}
              value={draft.respondentType}
              disabled={respondentTypesLoading}
              placeholder={respondentTypesLoading ? f.loading : f.selectRespondentType}
              options={respondentTypeOptions}
              onChange={(id) => {
                const name = respondentTypeOptions.find((o) => o.id === id)?.label ?? lookupName(respondentTypes, id);
                setDraft((d) => ({
                  ...d,
                  respondentType: id,
                  respondentTypeName: name,
                }));
                clearErr('respondentType');
              }}
            />
            <FieldError message={errors.respondentType} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>{f.respondentAddress}</label>
            <textarea
              id="ag-respondentAddress"
              className={inpClass(Boolean(errors.respondentAddress))}
              rows={3}
              value={draft.respondentAddress}
              onChange={(e) => {
                setDraft((d) => ({ ...d, respondentAddress: maxLen('respondentAddress', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Respondent Address')}
            />
            <FieldError message={errors.respondentAddress} />
          </div>
          <div className="ccr-fg">
            <label>{f.numberOfRespondents}</label>
            <input
              id="ag-numberOfRespondents"
              className={inpClass(Boolean(errors.numberOfRespondents))}
              type="text"
              inputMode="numeric"
              pattern="\\d*"
              value={draft.numberOfRespondents}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d]/g, '');
                const limitExceeded = raw.length > MAX_NUMBER_OF_RESPONDENTS_DIGITS;
                const digits = raw.slice(0, MAX_NUMBER_OF_RESPONDENTS_DIGITS);
                setDraft((d) => ({ ...d, numberOfRespondents: digits }));
                const err = limitExceeded
                  ? maxCharLimitExceededError()
                  : numberOfRespondentsError(digits);
                setErrors((prev) => ({ ...prev, numberOfRespondents: err ?? '' }));
              }}
              placeholder={agEnterPlaceholder('Number of Respondents')}
            />
            <FieldError message={errors.numberOfRespondents} />
          </div>
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={8}
        onBack={() => nav(attorneyGeneralWizardPath(7, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(9, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
